import {APP_INITIALIZER, ErrorHandler, Injector, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {AuthenticationModule} from './authentication/authentication.module';
import {CoreModule} from './core/core.module';
import {SharedModule} from './shared/shared.module';
import {TranslateService, TranslateStore} from '@ngx-translate/core';
import {commonProperties} from '../assets/environments/environment.common';
import {DatePipe} from '@angular/common';
import { ActivityReportModule } from './activity-report/activity-report.module';
import {environment} from '../assets/environments/environment';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {defineLocale} from 'ngx-bootstrap/chronos';
import { frLocale, enGbLocale } from 'ngx-bootstrap/locale';
import * as Sentry from '@sentry/angular';

defineLocale('fr', frLocale);
defineLocale('en', enGbLocale);

export function initConfig(
  injector: Injector,
  logger: NGXLogger,
  translate: TranslateService
) {
  return () => {
    const initTasks: Promise<any>[] = [];
    // Logger config for non-production
    if (!environment.production) {
      const updatedConfig = logger.getConfigSnapshot();
      updatedConfig.level = NgxLoggerLevel.ERROR;
      logger.updateConfig(updatedConfig);
    }

    // JWT token validation
    const jwtHelper = injector.get(JwtHelperService);
    const router = injector.get(Router);
    const token = localStorage.getItem(commonProperties.token);
    if (token) {
      if (jwtHelper.isTokenExpired()) {
        localStorage.removeItem(commonProperties.token);
        router.navigate(['login']);
      }

      const xhr = new XMLHttpRequest();
      xhr.open('POST', environment.api_root + commonProperties.tokenValidation);
      xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

      const tokenValidationPromise = new Promise<void>((resolve) => {
        xhr.addEventListener('readystatechange', () => {
          if (xhr.readyState === XMLHttpRequest.DONE) {
            if (xhr.status !== 204) {
              localStorage.removeItem(commonProperties.token);
              router.navigate(['login']);
            }
            resolve();
          }
        });
      });

      xhr.send(JSON.stringify({ token, type: 'SA' }));
      initTasks.push(tokenValidationPromise);
    }

    // Add TranslateService loading
    initTasks.push(translate.use(localStorage.getItem('lang') || 'GB').toPromise());

    return Promise.all(initTasks);
  };
}

if (environment.env !== 'local') {
  Sentry.init({
    dsn: 'https://c88c66ec69fc995570b2207012f0de36@o4509627437350912.ingest.de.sentry.io/4509688747655248',
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      Sentry.browserTracingIntegration(),
    ],
    environment: environment.env,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // We recommend adjusting this value in production
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/api\.qa\.isupplier\.fr/, /^https:\/\/api\.demo\.isupplier\.fr/, /^https:\/\/api\.staging\.isupplier\.fr/, /^https:\/\/api\.isupplier\.fr/],
  });
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    SharedModule,
    AuthenticationModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.DEBUG,
      timestampFormat: 'dd/MM/yyyy HH:mm:ss'
    }),
    ActivityReportModule,
    FontAwesomeModule
  ],
  providers: [
    TranslateStore,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initConfig,
      multi: true,
      deps: [Injector, NGXLogger, TranslateService, Sentry.TraceService]
    },
    DatePipe
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
